import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import {
  api,
  type Project,
  type ProjectDetail,
  type ProjectListResponse,
  type ResponseMatrixItem,
} from "@/lib/api";

export const projectsKey = ["projects"] as const;
export const projectKey = (id: string) => ["project", id] as const;
export const matrixKey = (id: string) => ["project", id, "matrix"] as const;
export const matrixActivityKey = (itemId: string) => ["matrix", itemId, "activity"] as const;
export const dashboardStateKey = ["projects", "dashboard-state"] as const;

export function useProjects(
  params?: Parameters<typeof api.listProjects>[0],
): UseQueryResult<ProjectListResponse> {
  return useQuery({
    queryKey: [...projectsKey, params ?? {}],
    queryFn: () => api.listProjects(params),
  });
}

export function useProject(id: string | null) {
  return useQuery({
    queryKey: id ? projectKey(id) : ["project", null],
    queryFn: () => (id ? api.getProject(id) : Promise.reject(new Error("no id"))),
    enabled: Boolean(id),
    // Auto-poll every 5s while the project is missing rawRfpText OR summary
    // — covers the post-upload window when parse_file + parse_summary are
    // still running. Stops polling once both are populated.
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return 5000;
      const stillExtracting = !data.rawRfpText || !data.summary;
      return stillExtracting ? 5000 : false;
    },
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createProject,
    onSuccess(project) {
      qc.invalidateQueries({ queryKey: projectsKey });
      qc.setQueryData<Project>(projectKey(project.id), project);
    },
  });
}

export function useMatrixItems(projectId: string | null) {
  return useQuery({
    queryKey: projectId ? matrixKey(projectId) : ["project", null, "matrix"],
    queryFn: () => (projectId ? api.listMatrix(projectId) : Promise.reject(new Error("no id"))),
    enabled: Boolean(projectId),
  });
}

export function useCreateMatrixItem(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof api.createMatrixItem>[1]) =>
      api.createMatrixItem(projectId, body),
    onSuccess() {
      qc.invalidateQueries({ queryKey: matrixKey(projectId) });
      qc.invalidateQueries({ queryKey: projectKey(projectId) });
    },
  });
}

export function useUpdateMatrixItem(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      patch,
    }: {
      itemId: string;
      patch: Parameters<typeof api.updateMatrixItem>[1];
    }) => api.updateMatrixItem(itemId, patch),
    // Optimistic update — flick state immediately, roll back on error.
    async onMutate({ itemId, patch }) {
      await qc.cancelQueries({ queryKey: matrixKey(projectId) });
      const prev = qc.getQueryData<ResponseMatrixItem[]>(matrixKey(projectId));
      if (prev) {
        qc.setQueryData<ResponseMatrixItem[]>(
          matrixKey(projectId),
          prev.map((it) => (it.id === itemId ? { ...it, ...patch } : it)),
        );
      }
      return { prev };
    },
    onError(_err, _vars, ctx) {
      if (ctx?.prev) qc.setQueryData(matrixKey(projectId), ctx.prev);
    },
    onSettled() {
      qc.invalidateQueries({ queryKey: matrixKey(projectId) });
    },
  });
}

export function useDeleteMatrixItem(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => api.deleteMatrixItem(itemId),
    onSuccess() {
      qc.invalidateQueries({ queryKey: matrixKey(projectId) });
      qc.invalidateQueries({ queryKey: projectKey(projectId) });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteProject(id),
    onSuccess(_void, id) {
      qc.invalidateQueries({ queryKey: projectsKey });
      qc.removeQueries({ queryKey: projectKey(id) });
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.updateProject>[1] }) =>
      api.updateProject(id, body),
    onSuccess(_data, vars) {
      qc.invalidateQueries({ queryKey: projectKey(vars.id) });
      qc.invalidateQueries({ queryKey: projectsKey });
    },
  });
}

export function useDeleteFile(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) => api.deleteFile(projectId, fileId),
    onSuccess() {
      // Refetch the project so rfpFileId / rawRfpText / summary update.
      qc.invalidateQueries({ queryKey: projectKey(projectId) });
    },
  });
}

export function useMatrixItemActivity(
  itemId: string | null,
): UseQueryResult<import("@/lib/api").MatrixItemActivityResponse> {
  return useQuery({
    queryKey: itemId ? matrixActivityKey(itemId) : ["matrix", null, "activity"],
    queryFn: (): Promise<import("@/lib/api").MatrixItemActivityResponse> =>
      itemId ? api.listMatrixItemActivity(itemId) : Promise.reject(new Error("no id")),
    enabled: Boolean(itemId),
    // Refetch when the panel re-opens; otherwise we rely on cache + manual
    // invalidation after mutations from the same panel.
    staleTime: 5_000,
  });
}

export function useDashboardState() {
  return useQuery({
    queryKey: dashboardStateKey,
    queryFn: () => api.getDashboardState(),
    staleTime: 30_000,
  });
}

export type { Project, ProjectDetail, ResponseMatrixItem };
